import React from 'react'
import { DetailRow } from '@pandiafi/components'
import { TitleController, Heading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import {
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { useContractPaths } from '../../hooks/useContractPaths/useContractPaths'
import { BuildingViewDataLoader } from './BuildingViewDataLoader'
import { BuildingViewErrorHandler } from '../BulletinView/BuildingViewErrorHandler'
import { BuildingInfoType, HeatingMethod } from '../../../common/validations'
import { formatDatePeriod } from '../../utils/dateUtils'

export const BuildingView = () => {
  const { t } = useTranslation(['common', 'titles', 'documents'])
  const { frontPagePath } = useContractPaths()
  return (
    <TitleController title={t('titles:building')}>
      <ResidentAppView
        title={t('common:views.building')}
        backLink={
          <ResidentAppBackLink href={frontPagePath}>
            {t('common:views.home')}
          </ResidentAppBackLink>
        }
      >
        <ResidentAppCard>
          <BuildingViewErrorHandler>
            <BuildingViewDataLoader>
              {({ buildingInfo }) => (
                <BuildingInfoSection buildingInfo={buildingInfo} />
              )}
            </BuildingViewDataLoader>
          </BuildingViewErrorHandler>
        </ResidentAppCard>
      </ResidentAppView>
    </TitleController>
  )
}

const BuildingInfoSection: React.FC<{ buildingInfo: BuildingInfoType }> = ({
  buildingInfo,
}) => {
  const { t } = useTranslation(['common'])
  return (
    <>
      <Heading element="h2" size="xl" className="pb-4">
        {resolveTitle(buildingInfo)}
      </Heading>
      {buildingInfo.realPropertyImageUrl && (
        <div className="w-full bg-[common-gray-3] mb-6 flex flex-col justify-center items-center rounded-xl overflow-hidden">
          <img
            className="w-full transition-transform opacity-100 h-auto"
            src={buildingInfo.realPropertyImageUrl}
            alt={t('common:buildingInfo.realPropertyPictureAlt')}
          />
        </div>
      )}
      <DetailRow title={t('common:buildingInfo.title')}>
        <DetailRow.Item
          title={t('common:buildingInfo.district')}
          content={buildingInfo?.address?.district}
        />
        <DetailRow.Item
          title={t('common:buildingInfo.constructionDate')}
          content={
            buildingInfo.constructionDate
              ? new Date(buildingInfo.constructionDate).getFullYear().toString()
              : null
          }
        />
        <DetailRow.Item
          title={t('common:buildingInfo.realPropertyAreasTotal')}
          content={buildingInfo.realPropertyAreasTotal}
        />
        <DetailRow.Item
          title={t('common:buildingInfo.heatingMethod')}
          content={
            <HeatingMethodText heatingMethod={buildingInfo.heatingMethod} />
          }
        />
        <DetailRow.Item
          title={t('common:buildingInfo.energyClass')}
          content={buildingInfo.energyClass}
        />
        <DetailRow.Item
          title={t('common:buildingInfo.energyCertificate')}
          content={formatDatePeriod(
            buildingInfo.energyCertificateStartDate,
            buildingInfo.energyCertificateEndDate
          )}
        />
      </DetailRow>
      {buildingInfo.internetProvider && (
        <InternetProviderSection
          internetProvider={buildingInfo.internetProvider}
        />
      )}
    </>
  )
}

type InternetProviderType = NonNullable<BuildingInfoType['internetProvider']>

const InternetProviderSection: React.FC<{
  internetProvider: InternetProviderType
}> = ({ internetProvider }) => {
  const { t } = useTranslation(['common'])
  return (
    <DetailRow title={t('common:buildingInfo.internetProvider')}>
      <p className="font-medium">{internetProvider.operatorName}</p>
      {internetProvider.internetConnection && (
        <DetailRow.Item
          title={t('common:buildingInfo.connectionSpeed')}
          content={internetProvider.internetConnection}
        />
      )}
      {internetProvider.links.map((link) => (
        <a
          key={link.url}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${link.title} (opens in new window)`}
          className="text-blue-600 hover:underline block mt-2"
        >
          {link.title}
        </a>
      ))}
    </DetailRow>
  )
}

const HeatingMethodText: React.FC<{ heatingMethod: HeatingMethod }> = ({
  heatingMethod,
}) => {
  const { t } = useTranslation(['common'])
  switch (heatingMethod) {
    case HeatingMethod.AIR_TO_WATER_HEAT_PUMP:
      return t('common:buildingInfo.heatingMethods.airToWaterHeatPump')
    case HeatingMethod.DISTRICT:
      return t('common:buildingInfo.heatingMethods.districtHeating')
    case HeatingMethod.DISTRICT_AND_EXHAUST_AIR_HEAT_PUMP:
      return t(
        'common:buildingInfo.heatingMethods.districtAndExhaustAirHeatPump'
      )
    case HeatingMethod.DISTRICT_AND_SOLAR:
      return t('common:buildingInfo.heatingMethods.districtAndSolar')
    case HeatingMethod.ELECTRICAL:
      return t('common:buildingInfo.heatingMethods.electricHeating')
    case HeatingMethod.GAS:
      return t('common:buildingInfo.heatingMethods.gas')
    case HeatingMethod.GEOTHERMAL:
      return t('common:buildingInfo.heatingMethods.geothermalHeating')
    case HeatingMethod.GEOTHERMAL_AND_SOLAR:
      return t('common:buildingInfo.heatingMethods.geothermalAndSolar')
    case HeatingMethod.SOLAR:
      return t('common:buildingInfo.heatingMethods.solar')
    case HeatingMethod.OIL:
      return t('common:buildingInfo.heatingMethods.oilHeating')
    case HeatingMethod.NATURAL_GAS:
      return t('common:buildingInfo.heatingMethods.naturalGasHeating')
    case HeatingMethod.NO_HEATING:
      return t('common:buildingInfo.heatingMethods.noHeating')
    case HeatingMethod.NOT_SET:
      return t('common:buildingInfo.heatingMethods.notSet')
    default:
      return null
  }
}

const resolveTitle = (buildingInfo: BuildingInfoType) =>
  `${buildingInfo.address?.streetName}${buildingInfo.address?.streetNumber ? ` ${buildingInfo.address?.streetNumber}` : ''}, ${buildingInfo.postalAddress?.postalCode ? ` ${buildingInfo.postalAddress?.postalCode}` : ''}${buildingInfo.postalAddress?.city ? ' ' : ''}${buildingInfo.postalAddress?.city}`
